import { styled, keyframes } from '@mui/material/styles';

const blue = '#0d4efe';
const teal = '#01ebb2';

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.96);
    opacity: 0.88;
  }
`;

const SIZE_MAP = {
  sm: { root: 24, ring: 2.5, logo: 12, plate: 16 },
  md: { root: 48, ring: 3.5, logo: 24, plate: 32 },
  lg: { root: 64, ring: 4, logo: 32, plate: 42 },
};

export const getSpinnerMetrics = (size = 'md') =>
  SIZE_MAP[size] || SIZE_MAP.md;

export const SpinnerRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== '$size',
})(({ $size }) => {
  const { root } = getSpinnerMetrics($size);
  return {
    position: 'relative',
    width: root,
    height: root,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (prefers-reduced-motion: reduce)': {
      '& .nky-spinner-ring': {
        animation: 'none',
      },
      '& .nky-spinner-plate': {
        animation: 'none',
      },
    },
  };
});

export const SpinnerRing = styled('div', {
  shouldForwardProp: (prop) => prop !== '$size',
})(({ $size }) => {
  const { root, ring } = getSpinnerMetrics($size);
  return {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    background: `conic-gradient(from 0deg, ${teal} 0%, ${blue} 42%, transparent 68%)`,
    WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), #000 calc(100% - ${ring}px + 0.5px))`,
    mask: `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), #000 calc(100% - ${ring}px + 0.5px))`,
    animation: `${spin} 0.85s linear infinite`,
    boxSizing: 'border-box',
    width: root,
    height: root,
    filter: 'drop-shadow(0 0 6px rgba(13, 78, 254, 0.28))',
  };
});

export const SpinnerPlate = styled('div', {
  shouldForwardProp: (prop) => !['$size', '$onDark'].includes(prop),
})(({ $size, $onDark }) => {
  const { plate } = getSpinnerMetrics($size);
  return {
    position: 'relative',
    zIndex: 1,
    width: plate,
    height: plate,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: $onDark
      ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.08) 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f7fafc 100%)',
    border: $onDark
      ? '1px solid rgba(1, 235, 178, 0.28)'
      : '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow: $onDark
      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 14px rgba(0, 0, 0, 0.25)'
      : 'inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 4px 14px rgba(13, 78, 254, 0.12)',
    animation: `${pulse} 1.6s ease-in-out infinite`,
    overflow: 'hidden',
  };
});

export const SpinnerLogo = styled('img', {
  shouldForwardProp: (prop) => !['$size', '$onDark'].includes(prop),
})(({ $size, $onDark }) => {
  const { logo } = getSpinnerMetrics($size);
  return {
    display: 'block',
    width: logo,
    height: logo,
    objectFit: 'contain',
    // Logo assets sit on black; screen knocks the black out on dark overlays.
    mixBlendMode: $onDark ? 'screen' : 'normal',
  };
});
